/**
 * Shared rules for the console's automatic focus — used by
 * `SearchAutofocusDirective` (search boxes) and `FormAutofocusDirective`
 * (Add/Update forms), so both decide "may I take focus now?" the same way.
 */

/**
 * When to look again after the first attempt. ng-bootstrap focuses its modal
 * window (or the first focusable inside it) once the open animation settles, and
 * a dropdown can still be laying out on the first tick, so one late retry catches
 * both without polling.
 */
export const AUTOFOCUS_RETRY_MS = 250;

/** Input types that take no typing — focus sitting on one is not "the user is typing". */
const NON_TEXT_INPUTS = new Set([
  'button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file', 'image', 'hidden',
]);

/** A control the user types into (caret-bearing). */
export function isTextEntry(el: Element): boolean {
  if ((el as HTMLElement).isContentEditable || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    return true;
  }
  return el instanceof HTMLInputElement && !NON_TEXT_INPUTS.has(el.type);
}

/** Rendered and not hidden from the user (CSS, `hidden`, `inert`, `aria-hidden`). */
export function isShown(el: HTMLElement): boolean {
  if (!el.isConnected || !el.getClientRects().length || getComputedStyle(el).visibility === 'hidden') {
    return false;
  }
  return !el.closest('[inert], [aria-hidden="true"]');
}

/**
 * Is something laid over the target — a loading veil, a backdrop — so a click on
 * it would land elsewhere? A caret placed under such a layer is one the user can
 * neither see nor reach, so focus waits until the layer is gone. Only the part of
 * the field inside the viewport can be hit-tested; a field below the fold counts
 * as uncovered.
 */
export function isCovered(target: HTMLElement): boolean {
  const r = target.getBoundingClientRect();
  const x = Math.min(Math.max(r.left + r.width / 2, 0), window.innerWidth - 1);
  const y = r.top + Math.min(r.height / 2, 8);
  if (y < 0 || y >= window.innerHeight) {
    return false;
  }
  const hit = document.elementFromPoint(x, y);
  return !!hit && !target.contains(hit) && !hit.contains(target);
}

/**
 * May `target` take focus right now without taking it away from the user?
 *   - not while another text field holds focus — the user is typing there;
 *   - not from behind an open modal;
 *   - not when it is hidden, disabled or read-only.
 */
export function canTakeFocus(target: HTMLElement, active: Element | null): boolean {
  if ((target as HTMLInputElement).disabled || (target as HTMLInputElement).readOnly || !isShown(target)) {
    return false;
  }
  if (active && active !== target && isTextEntry(active)) {
    return false;
  }
  const modals = document.querySelectorAll('ngb-modal-window, [aria-modal="true"]');
  const top = modals[modals.length - 1];
  return !top || top.contains(target);
}

/**
 * The late retry only reclaims focus parked on a container around the target
 * (where ng-bootstrap puts it after opening a modal), or on <body> when the first
 * attempt never landed — focus the user has moved anywhere else is left alone.
 */
export function retryMayReclaim(target: HTMLElement, active: Element | null, alreadyFocused: boolean): boolean {
  const onBody = !active || active === document.body;
  const onContainer = !!active && !onBody && active.contains(target);
  return onContainer || (onBody && !alreadyFocused);
}

/**
 * Where "the end" of a field's text is, for the caret.
 *
 * Normally `value.length` — after an Update form's existing value or a restored
 * query. But an input mask with `lazy: false` (imask: the ABN, phone numbers) keeps
 * its unfilled slots IN the value — an empty ABN's value is `__ ___ ___ ___` — so
 * `value.length` puts the caret after the last slot, where the mask accepts nothing
 * and every keystroke is dropped. There the end is the first unfilled slot: index 0
 * when empty, after the last digit when part-filled. A `_` followed by ordinary
 * text ("my_file") is not a slot and is left alone.
 */
export function caretEnd(input: HTMLInputElement | HTMLTextAreaElement): number {
  const v = input.value;
  const slot = v.indexOf('_');
  return slot >= 0 && /^[_\s\-().\/]*$/.test(v.slice(slot)) ? slot : v.length;
}

/** Focus with the caret at the end of any existing value (see `caretEnd`). */
export function focusAtEnd(target: HTMLElement, preventScroll: boolean): boolean {
  target.focus({preventScroll});
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    const end = caretEnd(target);
    try { target.setSelectionRange(end, end); } catch { /* type without a selection API */ }
  } else if (target.isContentEditable) {
    const sel = window.getSelection();
    if (sel) {
      sel.selectAllChildren(target);
      sel.collapseToEnd();
    }
  }
  return document.activeElement === target;
}
